import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Ticket as TicketIcon, Plus, Search, AlertTriangle, Send,
  Star, Inbox, Clock as ClockIcon, Users as UsersIcon,
  CheckCircle2, Tag, Pin, User, Settings2, Filter, X,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useToast } from "@/lib/toast";
import { Badge, Empty, SlideOver, Spinner, Field } from "@/components/UI";
import CustomerWelcomeTour, { shouldShowTour } from "@/components/CustomerWelcomeTour";
import { formatDate } from "@/lib/format";

const PRIORITIES = ["Low", "Medium", "High", "Urgent"];
const STATUSES = ["New", "Open", "In Progress", "Waiting on Customer", "Resolved", "Closed"];
const CATEGORIES = ["Hardware", "Software", "Network", "CCTV / Surveillance", "Cloud / SaaS", "Cybersecurity", "Email", "Other"];
const CHANNELS = ["email", "phone", "portal", "in-person", "whatsapp"];

const PRIORITY_TONE = { Low: "slate", Medium: "blue", High: "amber", Urgent: "rose" };
const STATUS_TONE = {
  New: "blue", Open: "amber", "In Progress": "violet",
  "Waiting on Customer": "slate", Resolved: "emerald", Closed: "slate",
};

const EMPTY = {
  title: "", description: "",
  client_name: "", client_contact_person: "", client_email: "", client_phone: "",
  cc_emails: [],
  category: "Other", priority: "Medium", channel: "email",
  assignee_id: "",
};

function timeUntil(dateStr) {
  if (!dateStr) return null;
  const ms = new Date(dateStr).getTime() - Date.now();
  const overdue = ms < 0;
  const abs = Math.abs(ms);
  const h = Math.floor(abs / 3600000);
  const m = Math.floor((abs % 3600000) / 60000);
  const text = h > 0 ? `${h}h ${m}m` : `${m}m`;
  return { overdue, text };
}

export default function Tickets() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState([]);
  const [search, setSearch] = useState("");
  const [view, setView] = useState("inbox"); // inbox|starred|today|team|pinned|resolved
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [selectedId, setSelectedId] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [selectedLoading, setSelectedLoading] = useState(false);
  const [composeMode, setComposeMode] = useState("public"); // public|internal
  const [composeBody, setComposeBody] = useState("");
  const [sending, setSending] = useState(false);
  const [editor, setEditor] = useState({ open: false, ticket: null });
  const [tourOpen, setTourOpen] = useState(false);
  const [starred, setStarred] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem("jero_starred_tickets") || "[]")); }
    catch { return new Set(); }
  });
  const [pinned, setPinned] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem("jero_pinned_tickets") || "[]")); }
    catch { return new Set(); }
  });
  const { user } = useAuth();
  const { toast } = useToast();

  const persistStarred = (next) => {
    setStarred(next);
    try { localStorage.setItem("jero_starred_tickets", JSON.stringify([...next])); }
    catch { /* ignore */ }
  };
  const toggleStar = (id) => {
    const n = new Set(starred);
    if (n.has(id)) n.delete(id); else n.add(id);
    persistStarred(n);
  };

  useEffect(() => {
    if (shouldShowTour(user)) setTourOpen(true);
  }, [user]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setTickets(await api.listTickets());
    } catch { toast("Failed to load tickets", "error"); }
    finally { setLoading(false); }
  }, [toast]);

  useEffect(() => {
    load();
    api.team().then(setMembers).catch(() => {});
  }, [load]);

  const filtered = useMemo(() => {
    let xs = tickets;
    if (view === "inbox") {
      xs = xs.filter((t) => !["Resolved", "Closed"].includes(t.status));
    } else if (view === "starred") {
      xs = xs.filter((t) => starred.has(t.id));
    } else if (view === "today") {
      const now = Date.now();
      xs = xs.filter((t) =>
        !["Resolved", "Closed"].includes(t.status) &&
        t.resolution_due_at &&
        new Date(t.resolution_due_at).getTime() - now < 24 * 3600 * 1000
      );
    } else if (view === "team") {
      xs = xs.filter((t) => !t.assignee_id && !["Resolved", "Closed"].includes(t.status));
    } else if (view === "pinned") {
      xs = xs.filter((t) => pinned.has(t.id));
    } else if (view === "resolved") {
      xs = xs.filter((t) => ["Resolved", "Closed"].includes(t.status));
    }
    if (priorityFilter !== "all") xs = xs.filter((t) => t.priority === priorityFilter);
    if (search) {
      const s = search.toLowerCase();
      xs = xs.filter((t) =>
        [t.title, t.serial, t.client_name, t.client_email, t.description]
          .some((v) => (v || "").toLowerCase().includes(s))
      );
    }
    // Sort: Urgent → SLA soonest → newest
    return [...xs].sort((a, b) => {
      const rank = { Urgent: 0, High: 1, Medium: 2, Low: 3 };
      const p = (rank[a.priority] ?? 4) - (rank[b.priority] ?? 4);
      if (p !== 0) return p;
      const at = a.resolution_due_at ? new Date(a.resolution_due_at).getTime() : Infinity;
      const bt = b.resolution_due_at ? new Date(b.resolution_due_at).getTime() : Infinity;
      return at - bt;
    });
  }, [tickets, view, priorityFilter, search, starred, pinned]);

  const viewCounts = useMemo(() => ({
    inbox: tickets.filter((t) => !["Resolved", "Closed"].includes(t.status)).length,
    starred: [...starred].filter((id) => tickets.find((t) => t.id === id)).length,
    today: tickets.filter((t) =>
      !["Resolved", "Closed"].includes(t.status) && t.resolution_due_at &&
      new Date(t.resolution_due_at).getTime() - Date.now() < 24 * 3600 * 1000
    ).length,
    team: tickets.filter((t) => !t.assignee_id && !["Resolved", "Closed"].includes(t.status)).length,
    pinned: [...pinned].filter((id) => tickets.find((t) => t.id === id)).length,
    resolved: tickets.filter((t) => ["Resolved", "Closed"].includes(t.status)).length,
  }), [tickets, starred, pinned]);

  // When a ticket is selected in the list, fetch the full record (with comments)
  useEffect(() => {
    if (!selectedId) { setSelectedTicket(null); return; }
    setSelectedLoading(true);
    api.getTicket(selectedId)
      .then((t) => { setSelectedTicket(t); setComposeBody(""); })
      .catch(() => toast("Failed to load ticket", "error"))
      .finally(() => setSelectedLoading(false));
  }, [selectedId, toast]);

  // Auto-select first ticket on desktop; on mobile keep the list visible so the user must tap.
  useEffect(() => {
    const isDesktop =
      typeof window !== "undefined" && window.matchMedia("(min-width: 1024px)").matches;
    if (!isDesktop) return;
    if (!selectedId && filtered.length > 0) setSelectedId(filtered[0].id);
    if (selectedId && !filtered.find((t) => t.id === selectedId) && filtered.length > 0) {
      setSelectedId(filtered[0].id);
    }
  }, [filtered, selectedId]);

  const sendReply = async () => {
    if (!selectedTicket || !composeBody.trim()) return;
    setSending(true);
    try {
      await api.addTicketComment(selectedTicket.id, {
        body: composeBody.trim(),
        public: composeMode === "public",
      });
      const fresh = await api.getTicket(selectedTicket.id);
      setSelectedTicket(fresh);
      setComposeBody("");
      toast(composeMode === "public" ? "Reply sent" : "Internal note added", "success");
      // refresh list to update updated_at
      load();
    } catch (e) {
      toast(e?.response?.data?.detail || "Failed to send", "error");
    } finally {
      setSending(false);
    }
  };

  const save = async (form, ticket) => {
    try {
      if (ticket?.id) {
        await api.updateTicket(ticket.id, form);
        toast("Ticket updated", "success");
      } else {
        const created = await api.createTicket(form);
        toast(`Created ${created.serial}`, "success");
      }
      setEditor({ open: false, ticket: null });
      load();
    } catch (e) {
      toast(e?.response?.data?.detail || "Save failed", "error");
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete ${t.serial}?`)) return;
    try { await api.deleteTicket(t.id); toast("Deleted", "success"); load(); }
    catch { toast("Delete failed", "error"); }
  };

  const priorityBadge = (p) => {
    const cls = {
      Urgent: "bg-red-100 text-red-700",
      High: "bg-amber-100 text-amber-700",
      Medium: "bg-emerald-100 text-emerald-700",
      Low: "bg-slate-100 text-slate-600",
    }[p] || "bg-slate-100 text-slate-600";
    return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${cls}`}>{(p || "").toUpperCase()}</span>;
  };

  const VIEWS = [
    { key: "inbox", label: "Inbox", icon: Inbox },
    { key: "starred", label: "Starred", icon: Star },
    { key: "today", label: "Due today", icon: ClockIcon },
    { key: "team", label: "Team queue", icon: UsersIcon },
    { key: "pinned", label: "Pinned", icon: Pin },
    { key: "resolved", label: "Resolved", icon: CheckCircle2 },
  ];

  const TAGS = ["hardware", "cctv", "network", "cybersec", "cloud", "email"];

  return (
    <div
      data-testid="page-tickets"
      className="flex flex-col lg:flex-row h-[calc(100vh-70px)] -mx-6 -mt-4 border-t border-slate-200 bg-white overflow-hidden"
      style={{ marginLeft: "-1.5rem", marginRight: "-1.5rem" }}
    >
      <CustomerWelcomeTour open={tourOpen} onClose={() => setTourOpen(false)} />

      {/* ============ LEFT RAIL — Views (desktop only) ============ */}
      <aside className="hidden lg:flex w-52 shrink-0 border-r border-slate-100 p-3 flex-col overflow-y-auto">
        <div className="text-emerald-700 font-bold text-sm mb-3 flex items-center gap-1.5">
          <TicketIcon size={16} /> Service Desk
        </div>
        <button
          data-testid="new-ticket"
          onClick={() => setEditor({ open: true, ticket: null })}
          className="w-full bg-emerald-700 text-white text-sm font-semibold py-2 rounded-lg mb-4 hover:bg-emerald-800 transition"
        >
          + New ticket
        </button>
        <nav className="space-y-0.5 text-sm">
          {VIEWS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setView(key)}
              data-testid={`view-${key}`}
              className={`w-full px-2 py-1.5 rounded-md flex items-center gap-2 text-left transition ${
                view === key ? "bg-emerald-50 text-emerald-800 font-semibold" : "text-slate-700 hover:bg-slate-50"
              }`}
            >
              <Icon size={14} />
              <span className="flex-1">{label}</span>
              <span className={`text-[11px] ${view === key ? "text-emerald-700 font-bold" : "text-slate-400"}`}>
                {viewCounts[key]}
              </span>
            </button>
          ))}
        </nav>

        <div className="mt-5 text-[11px] uppercase tracking-widest text-slate-500 font-bold mb-2 flex items-center gap-1">
          <Tag size={10} /> Tags
        </div>
        <div className="space-y-1 text-xs text-slate-600">
          {TAGS.map((t) => (
            <button
              key={t}
              onClick={() => setSearch(t)}
              className="px-2 py-1 rounded hover:bg-slate-50 w-full text-left"
              data-testid={`tag-${t}`}
            >
              #{t}
            </button>
          ))}
        </div>

        <div className="flex-1" />
      </aside>

      {/* ============ MIDDLE — Ticket list ============ */}
      <section
        className={`${
          selectedId ? "hidden lg:flex" : "flex"
        } w-full lg:w-[380px] shrink-0 border-r border-slate-100 flex-col overflow-hidden`}
      >
        <div className="p-3 border-b border-slate-100 sticky top-0 bg-white z-10 space-y-2">
          {/* Mobile-only: Service Desk title + New ticket + view chips */}
          <div className="lg:hidden flex items-center justify-between">
            <div className="text-emerald-700 font-bold text-sm flex items-center gap-1.5">
              <TicketIcon size={16} /> Service Desk
            </div>
            <button
              data-testid="new-ticket-mobile"
              onClick={() => setEditor({ open: true, ticket: null })}
              className="bg-emerald-700 text-white text-xs font-semibold px-3 py-1.5 rounded-lg hover:bg-emerald-800"
            >
              + New
            </button>
          </div>
          <div className="lg:hidden flex gap-1.5 overflow-x-auto -mx-1 px-1 pb-1">
            {VIEWS.map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => setView(key)}
                data-testid={`view-${key}-mobile`}
                className={`shrink-0 text-[11px] font-semibold px-2.5 py-1 rounded-full flex items-center gap-1 whitespace-nowrap transition ${
                  view === key ? "bg-emerald-700 text-white" : "bg-slate-100 text-slate-700"
                }`}
              >
                <Icon size={11} />
                {label}
                <span className={`text-[10px] font-bold ml-0.5 ${view === key ? "text-emerald-100" : "text-slate-500"}`}>
                  {viewCounts[key]}
                </span>
              </button>
            ))}
          </div>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="ticket-search"
              className="w-full text-sm pl-9 pr-3 py-2 border border-slate-200 rounded-md"
              placeholder="Search tickets…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-2 text-[11px]">
            <span className="text-slate-500 flex items-center gap-1"><Filter size={10} /> Priority</span>
            <select
              className="text-[11px] border border-slate-200 rounded-md px-1.5 py-0.5"
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
              data-testid="priority-filter"
            >
              <option value="all">All</option>
              {PRIORITIES.map((p) => <option key={p}>{p}</option>)}
            </select>
            <span className="text-slate-400 ml-auto">{filtered.length}</span>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-6 flex items-center justify-center text-sm text-slate-500">
              <Spinner /> &nbsp;Loading…
            </div>
          ) : filtered.length === 0 ? (
            <Empty icon={TicketIcon} title="No tickets" description="Nothing matches this view." />
          ) : (
            filtered.map((t) => {
              const sla = timeUntil(t.resolution_due_at);
              const isSelected = t.id === selectedId;
              return (
                <button
                  key={t.id}
                  onClick={() => setSelectedId(t.id)}
                  data-testid={`ticket-row-${t.id}`}
                  className={`w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-slate-50 transition ${
                    isSelected ? "bg-emerald-50/60 border-l-4 border-l-emerald-700" : "border-l-4 border-l-transparent"
                  }`}
                >
                  <div className="flex justify-between items-start gap-2">
                    {priorityBadge(t.priority)}
                    <span className="text-[10px] text-slate-500">{formatDate(t.updated_at)}</span>
                  </div>
                  <div className={`font-semibold text-sm mt-1 line-clamp-1 ${isSelected ? "text-slate-900" : "text-slate-800"}`}>
                    {t.title}
                  </div>
                  <div className="text-xs text-slate-600 truncate mt-0.5">
                    {t.client_name || "Unknown"} · <span className="font-mono text-[10px]">{t.serial}</span>
                    {(t.cc_emails || []).length > 0 && ` · +${(t.cc_emails || []).length} CC`}
                  </div>
                  {t.description && (
                    <div className="text-xs text-slate-500 mt-1 line-clamp-1">{t.description}</div>
                  )}
                  {sla && !["Resolved", "Closed"].includes(t.status) && (
                    <div className={`text-[10px] mt-1 font-semibold ${sla.overdue ? "text-red-600" : "text-slate-500"}`}>
                      {sla.overdue ? `⚠ Overdue by ${sla.text}` : `SLA in ${sla.text}`}
                    </div>
                  )}
                </button>
              );
            })
          )}
        </div>
      </section>

      {/* ============ RIGHT — Conversation ============ */}
      <section
        className={`${
          !selectedId ? "hidden lg:flex" : "flex"
        } flex-1 flex-col overflow-hidden bg-slate-50/30`}
      >
        {!selectedTicket ? (
          <div className="flex-1 flex items-center justify-center text-sm text-slate-500">
            {selectedLoading ? <><Spinner /> &nbsp;Loading…</> : "Select a ticket to view the conversation."}
          </div>
        ) : (
          <>
            {/* Mobile-only: Back-to-list bar */}
            <div className="lg:hidden border-b border-slate-100 px-3 py-2 bg-white flex items-center gap-2">
              <button
                data-testid="ticket-back-to-list"
                type="button"
                onClick={() => { setSelectedId(null); setSelectedTicket(null); }}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-600 hover:text-slate-900 px-2 py-1 -ml-1"
              >
                <X size={14} /> Back to tickets
              </button>
              <span className="text-[10px] text-slate-400 font-mono ml-auto">{selectedTicket.serial}</span>
            </div>
            {/* Ticket header */}
            <header className="border-b border-slate-100 px-4 lg:px-6 py-3 lg:py-4 bg-white flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  {priorityBadge(selectedTicket.priority)}
                  <span className="text-xs text-slate-500 font-mono hidden lg:inline">{selectedTicket.serial}</span>
                  {selectedTicket.resolution_due_at && !["Resolved", "Closed"].includes(selectedTicket.status) && (() => {
                    const s = timeUntil(selectedTicket.resolution_due_at);
                    return (
                      <span className={`text-xs ${s?.overdue ? "text-red-600 font-semibold" : "text-slate-500"}`}>
                        · SLA {s?.overdue ? `breached by ${s.text}` : `in ${s.text}`}
                      </span>
                    );
                  })()}
                </div>
                <h2 className="text-base lg:text-lg font-bold text-slate-900 line-clamp-2 break-words">{selectedTicket.title}</h2>
                <div className="flex gap-2 lg:gap-4 text-xs text-slate-600 mt-1 flex-wrap">
                  <span className="break-all"><strong>From:</strong> {selectedTicket.client_contact_person || selectedTicket.client_name || "—"} · {selectedTicket.client_email || "—"}</span>
                  {(selectedTicket.cc_emails || []).length > 0 && (
                    <span className="break-all"><strong>CC:</strong> {(selectedTicket.cc_emails || []).join(", ")}</span>
                  )}
                  <span><strong>Assigned:</strong> {members.find((m) => m.id === selectedTicket.assignee_id)?.full_name || members.find((m) => m.id === selectedTicket.assignee_id)?.email || "Unassigned"}</span>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0 self-end sm:self-start">
                <button
                  onClick={() => toggleStar(selectedTicket.id)}
                  className="p-2 hover:bg-slate-100 rounded-md"
                  title={starred.has(selectedTicket.id) ? "Unstar" : "Star"}
                  data-testid="star-btn"
                >
                  <Star
                    size={16}
                    className={starred.has(selectedTicket.id) ? "text-amber-500 fill-amber-500" : "text-slate-400"}
                  />
                </button>
                <button
                  onClick={() => setEditor({ open: true, ticket: selectedTicket })}
                  className="text-xs px-3 py-1.5 border border-slate-200 rounded-md hover:bg-slate-50"
                  data-testid="edit-details"
                >
                  <Settings2 size={12} className="inline mr-1" /> Details
                </button>
              </div>
            </header>

            {/* Thread */}
            <div className="flex-1 overflow-y-auto p-4 lg:p-6 space-y-4">
              {/* Original description (customer request) */}
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-red-100 text-red-700 flex items-center justify-center text-xs font-bold shrink-0">
                  {(selectedTicket.client_contact_person || selectedTicket.client_name || "?").slice(0, 2).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-slate-500 mb-1 break-words">
                    <b>{selectedTicket.client_contact_person || selectedTicket.client_name || "Customer"}</b>
                    &nbsp;· {selectedTicket.client_name} · {formatDate(selectedTicket.created_at)} · Customer
                  </div>
                  <div className="bg-slate-100 text-slate-800 rounded-2xl px-4 py-3 text-sm max-w-full sm:max-w-[85%] break-words whitespace-pre-wrap">
                    {selectedTicket.description || "(no description)"}
                  </div>
                </div>
              </div>

              {/* Comments — safe to use index-as-key: comments are append-only from server */}
              {(selectedTicket.comments || []).map((c, i) => {
                const isPublic = c.public;
                const author = members.find((m) => m.id === c.author_id);
                const initials = (c.author_name || author?.full_name || author?.email || "?").slice(0, 2).toUpperCase();
                return (
                  // eslint-disable-next-line react/no-array-index-key
                  <div key={c.id || `${c.created_at || ""}-${i}`} className="flex gap-3">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                        isPublic ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
                      }`}
                    >
                      {initials}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-slate-500 mb-1">
                        <b>{c.author_name}</b> · {formatDate(c.created_at)} ·
                        &nbsp;{isPublic ? (
                          <span className="text-emerald-700 font-semibold">Public reply</span>
                        ) : (
                          <span className="text-amber-600 font-semibold">Internal note</span>
                        )}
                      </div>
                      <div className={`rounded-2xl px-4 py-3 text-sm max-w-full sm:max-w-[85%] whitespace-pre-wrap break-words ${
                        isPublic ? "bg-emerald-50 border border-emerald-200 text-emerald-900" : "bg-amber-50 border border-amber-200 text-amber-900"
                      }`}>
                        {c.body}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Composer */}
            <div className="border-t border-slate-100 p-4 bg-slate-50">
              <div className="bg-white border border-slate-200 rounded-lg p-3">
                <div className="text-xs mb-2 flex gap-3 border-b border-slate-100 pb-2">
                  <button
                    onClick={() => setComposeMode("public")}
                    data-testid="tab-public"
                    className={composeMode === "public"
                      ? "text-emerald-700 font-semibold border-b-2 border-emerald-700 pb-1 -mb-2 px-1"
                      : "text-slate-500 hover:text-slate-800 px-1"}
                  >
                    Public reply
                  </button>
                  <button
                    onClick={() => setComposeMode("internal")}
                    data-testid="tab-internal"
                    className={composeMode === "internal"
                      ? "text-amber-700 font-semibold border-b-2 border-amber-700 pb-1 -mb-2 px-1"
                      : "text-slate-500 hover:text-slate-800 px-1"}
                  >
                    Internal note
                  </button>
                </div>
                <textarea
                  data-testid="compose-body"
                  className="w-full text-sm outline-none resize-none py-1"
                  rows={3}
                  placeholder={composeMode === "public"
                    ? "Type a reply to the customer…"
                    : "Type an internal note (only visible to the team)…"}
                  value={composeBody}
                  onChange={(e) => setComposeBody(e.target.value)}
                  onKeyDown={(e) => {
                    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") sendReply();
                  }}
                />
                <div className="flex items-center justify-between mt-2 text-xs">
                  <span className="text-slate-400">
                    {composeMode === "public"
                      ? "Sent to customer + CC list · ⌘↵ to send"
                      : "Team-only · ⌘↵ to save"}
                  </span>
                  <button
                    onClick={sendReply}
                    disabled={sending || !composeBody.trim()}
                    data-testid="send-reply"
                    className={`px-3 py-1.5 rounded-md font-semibold text-white text-xs ${
                      composeMode === "public" ? "bg-emerald-700 hover:bg-emerald-800" : "bg-amber-600 hover:bg-amber-700"
                    } disabled:opacity-50`}
                  >
                    {sending ? "Sending…" : composeMode === "public" ? (<><Send size={12} className="inline" /> Send reply</>) : "Save note"}
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </section>

      <TicketEditor
        open={editor.open}
        ticket={editor.ticket}
        members={members}
        onClose={() => setEditor({ open: false, ticket: null })}
        onSave={(form) => save(form, editor.ticket)}
        onChanged={() => { load(); if (selectedId) api.getTicket(selectedId).then(setSelectedTicket); }}
      />
    </div>
  );
}

function Stat({ label, value, icon: Icon, tone = "slate" }) {
  const tones = {
    slate: { bg: "var(--ink-100)", fg: "var(--ink-700)" },
    amber: { bg: "var(--amber-bg)", fg: "var(--amber-fg)" },
    rose: { bg: "var(--rose-bg)", fg: "var(--rose-fg)" },
  };
  const t = tones[tone] || tones.slate;
  return (
    <div className="stat">
      <div className="flex items-start justify-between mb-2">
        <div className="stat-label">{label}</div>
        <div className="w-8 h-8 rounded-lg flex items-center justify-center"
             style={{ background: t.bg, color: t.fg }}>
          <Icon size={14} />
        </div>
      </div>
      <div className="stat-value">{value}</div>
    </div>
  );
}

function TicketEditor({ open, ticket, members, onClose, onSave, onChanged }) {
  const [form, setForm] = useState(EMPTY);
  const [tab, setTab] = useState("details");
  const [comment, setComment] = useState("");
  const [commentPublic, setCommentPublic] = useState(false);
  const [busy, setBusy] = useState(false);
  const { user } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    // For customers, pre-fill contact fields from their profile so they don't
    // re-type it on every ticket.
    if (!ticket && user?.role === "customer") {
      setForm({
        ...EMPTY,
        client_name: user.company_name || user.full_name || "",
        client_contact_person: user.full_name || "",
        client_email: user.email || "",
        client_phone: user.phone || "",
      });
    } else {
      setForm(ticket || EMPTY);
    }
    setTab("details");
    setComment("");
    setCommentPublic(false);
  }, [ticket, open, user]);

  const upd = (patch) => setForm((f) => ({ ...f, ...patch }));

  const sendComment = async () => {
    if (!comment.trim() || !ticket?.id) return;
    setBusy(true);
    try {
      await api.addTicketComment(ticket.id, { body: comment, public: commentPublic });
      toast(commentPublic ? "Public reply added" : "Internal note added", "success");
      setComment("");
      onChanged();
      onClose();
    } catch {
      toast("Comment failed", "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <SlideOver
      open={open}
      onClose={onClose}
      title={ticket?.id ? `${ticket.serial}` : "New Ticket"}
      subtitle={ticket?.id ? ticket.title : "Capture the issue and contact details"}
      footer={
        tab === "details" ? (
          <>
            <button onClick={onClose} className="btn btn-ghost">Cancel</button>
            <button data-testid="save-ticket" onClick={() => onSave(form)} className="btn btn-primary">
              {ticket?.id ? "Save changes" : "Create ticket"}
            </button>
          </>
        ) : null
      }
    >
      {/* Tabs */}
      {ticket?.id && (
        <div className="flex gap-1 border-b border-slate-100 mb-4 -mt-1">
          {[
            ["details", "Details"],
            ["activity", `Activity${ticket?.comments?.length ? ` (${ticket.comments.length})` : ""}`],
          ].map(([k, label]) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              data-testid={`tab-${k}`}
              className={`px-3 py-2 text-sm font-semibold border-b-2 ${
                tab === k ? "" : "border-transparent muted hover:text-slate-700"
              }`}
              style={tab === k ? { borderColor: "var(--green-600)", color: "var(--green-700)" } : {}}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {tab === "details" ? (
        <div className="space-y-4">
          <Field label="Title *">
            <input data-testid="ticket-title" className="input" value={form.title}
                   onChange={(e) => upd({ title: e.target.value })} />
          </Field>

          <Field label="Description">
            <textarea
              data-testid="ticket-desc"
              rows={4}
              className="textarea"
              value={form.description}
              onChange={(e) => upd({ description: e.target.value })}
              placeholder="What's the issue? Steps to reproduce, error messages…"
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Category">
              <select className="select" value={form.category} onChange={(e) => upd({ category: e.target.value })}>
                {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Channel">
              <select className="select" value={form.channel} onChange={(e) => upd({ channel: e.target.value })}>
                {CHANNELS.map((c) => <option key={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Priority *">
              <select data-testid="ticket-priority" className="select" value={form.priority}
                      onChange={(e) => upd({ priority: e.target.value })}>
                {PRIORITIES.map((p) => <option key={p}>{p}</option>)}
              </select>
            </Field>
            {ticket?.id && (
              <Field label="Status">
                <select data-testid="ticket-status" className="select" value={form.status || "New"}
                        onChange={(e) => upd({ status: e.target.value })}>
                  {STATUSES.map((s) => <option key={s}>{s}</option>)}
                </select>
              </Field>
            )}
            <Field label="Assignee">
              <select className="select" value={form.assignee_id || ""}
                      onChange={(e) => upd({ assignee_id: e.target.value || null })}>
                <option value="">Unassigned</option>
                {members.map((m) => (
                  <option key={m.id} value={m.id}>{m.full_name || m.email}</option>
                ))}
              </select>
            </Field>
          </div>

          <div className="border-t border-slate-100 pt-4">
            <div className="eyebrow mb-3">Customer</div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Client name">
                <input className="input" value={form.client_name}
                       onChange={(e) => upd({ client_name: e.target.value })} />
              </Field>
              <Field label="Contact person">
                <input className="input" value={form.client_contact_person}
                       onChange={(e) => upd({ client_contact_person: e.target.value })} />
              </Field>
              <Field label="Email">
                <input className="input" value={form.client_email}
                       onChange={(e) => upd({ client_email: e.target.value })} />
              </Field>
              <Field label="Phone">
                <input className="input" value={form.client_phone}
                       onChange={(e) => upd({ client_phone: e.target.value })} />
              </Field>
            </div>
            <div className="mt-3">
              <Field label="CC colleagues (comma-separated emails)">
                <input
                  className="input"
                  placeholder="teammate@yourbank.co.ug, manager@yourbank.co.ug"
                  value={(form.cc_emails || []).join(", ")}
                  onChange={(e) => upd({
                    cc_emails: e.target.value
                      .split(/[,;\s]+/)
                      .map((s) => s.trim())
                      .filter((s) => s.includes("@")),
                  })}
                  data-testid="ticket-cc-emails"
                />
                <div className="text-[11px] muted mt-1">
                  Everyone you list will be copied on every reply, status change and comment for this ticket.
                </div>
              </Field>
            </div>
          </div>

          {ticket?.id && (
            <div className="border-t border-slate-100 pt-4 grid grid-cols-2 gap-3 text-xs">
              <div>
                <div className="eyebrow">Response due</div>
                <div className="mt-1">{ticket.response_due_at ? new Date(ticket.response_due_at).toLocaleString() : "—"}</div>
              </div>
              <div>
                <div className="eyebrow">Resolution due</div>
                <div className="mt-1">{ticket.resolution_due_at ? new Date(ticket.resolution_due_at).toLocaleString() : "—"}</div>
              </div>
              <div>
                <div className="eyebrow">Created</div>
                <div className="mt-1">{new Date(ticket.created_at).toLocaleString()}</div>
              </div>
              <div>
                <div className="eyebrow">Resolved</div>
                <div className="mt-1">{ticket.resolved_at ? new Date(ticket.resolved_at).toLocaleString() : "—"}</div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="space-y-3 max-h-[400px] overflow-y-auto">
            {(ticket.comments || []).length === 0 ? (
              <div className="text-sm muted text-center py-6">No activity yet</div>
            ) : (
              ticket.comments.map((c) => (
                <div key={c.id} className={`rounded-lg p-3 border ${c.public ? "bg-[var(--green-50)] border-[var(--green-100)]" : "bg-amber-50/50 border-amber-100"}`}>
                  <div className="flex items-center justify-between gap-2 text-[11px] mb-1.5">
                    <div className="flex items-center gap-2">
                      <div className="avatar avatar-sm" style={{ width: 22, height: 22, fontSize: 10 }}>
                        {(c.author_name || "?").charAt(0).toUpperCase()}
                      </div>
                      <span className="font-semibold text-slate-700">{c.author_name}</span>
                      {c.public ? (
                        <Badge tone="green">Public reply</Badge>
                      ) : (
                        <Badge tone="amber">Internal note</Badge>
                      )}
                    </div>
                    <span className="muted">{new Date(c.created_at).toLocaleString()}</span>
                  </div>
                  <div className="text-sm whitespace-pre-wrap text-slate-800">{c.body}</div>
                </div>
              ))
            )}
          </div>

          <div className="border-t border-slate-100 pt-4">
            <Field label={commentPublic ? "Reply to customer" : "Internal note (team only)"}>
              <textarea
                data-testid="comment-body"
                rows={3}
                className="textarea"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={commentPublic ? "Type your reply to the customer…" : "Internal note visible only to your team…"}
              />
            </Field>
            <div className="flex items-center justify-between mt-2">
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="comment-public"
                  checked={commentPublic}
                  onChange={(e) => setCommentPublic(e.target.checked)}
                />
                <Send size={12} />
                {commentPublic ? "Public reply (sets first-response time)" : "Internal note"}
              </label>
              <button
                data-testid="send-comment"
                onClick={sendComment}
                disabled={!comment.trim() || busy}
                className="btn btn-primary btn-sm"
              >
                <Send size={12} /> {busy ? "Sending…" : (commentPublic ? "Send reply" : "Add note")}
              </button>
            </div>
          </div>
        </div>
      )}
    </SlideOver>
  );
}
